import React, {FC, useEffect, useState} from 'react'
import {CustomModal} from '_metronic/partials/modals/CustomModal'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import Input from 'app/components/Input'
import Select from '../Select'
import {MenuListProps, OptionProps, SingleValue, SingleValueProps} from 'react-select'
import {toAbsoluteUrl} from '_metronic/helpers'
import {FrameworkDataType} from '../Compliance'
import {useAuth} from 'app/modules/auth'
import {deleteObject, getDownloadURL, ref, uploadBytes} from 'firebase/storage'
import db, {storage} from 'services/firebase'
import Dropzone from 'react-dropzone'
import {FilterOptionOption} from 'react-select/dist/declarations/src/filters'
import {addDoc, collection, doc, serverTimestamp, setDoc, updateDoc} from '@firebase/firestore'
import Button from 'app/components/Button'
import {showSuccessToast} from 'app/components/ShowToast'

type Props = {
  isModalOpen: boolean
  hideModal: () => void
  isEditMode: boolean
  framework?: FrameworkDataType
  availableFrameworks: CustomOptionType[]
}

type CustomOptionType = {
  id?: string
  title: string
  logoURL?: string
}

const statusArr = [
  {title: 'Compliant', iconPath: '/media/icon/circle-check.svg'},
  {title: 'Ongoing', iconPath: '/media/icon/loader.svg'},
]

const getImageDimensions = (file: File): Promise<{width: number; height: number}> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => resolve({width: img.width, height: img.height})
      img.onerror = reject
      if (typeof e.target?.result === 'string') {
        img.src = e.target.result
      } else {
        reject('File reading failed')
      }
    }
    reader.readAsDataURL(file)
  })
}

const CustomOptionComponent = (props: OptionProps<CustomOptionType>) => {
  const {data, innerRef, innerProps} = props

  return (
    <div
      ref={innerRef}
      className='d-flex align-items-center cursor-pointer'
      style={{
        gap: '8px',
      }}
      {...innerProps}
    >
      <img
        alt={`${data.title}-logo`}
        src={toAbsoluteUrl(data.logoURL as string)}
        className=''
        style={{height: '16px', width: '16px'}}
      />
      <span className='text-dark fs-6'>{data.title}</span>
    </div>
  )
}
const menuListComponent = (props: MenuListProps<CustomOptionType>) => {
  const {children} = props

  const standardChildren: React.ReactNode[] = []
  let customChild: React.ReactNode | null = null

  React.Children.forEach(children, (child: any) => {
    if (child?.props?.data?.title.toLowerCase() === 'custom framework') {
      customChild = child
    } else {
      standardChildren.push(child)
    }
  })

  return (
    <div
      style={{
        padding: '16px',
      }}
    >
      <div
        style={{
          maxHeight: '150px',
          overflowY: 'auto',
          display: 'flex',
          gap: '12px',
          flexDirection: 'column',
        }}
      >
        {standardChildren}
      </div>

      {customChild &&
        (standardChildren.length > 0 ? (
          <div
            style={{
              marginTop: '12px',
              borderTop: '1px solid #E5E7EB',
              paddingTop: '12px',
            }}
          >
            {customChild}
          </div>
        ) : (
          <div>{customChild}</div>
        ))}
    </div>
  )
}

const SingleValueComponent = (props: SingleValueProps<CustomOptionType>) => {
  const {data, innerProps} = props
  return (
    <div
      className='d-flex align-items-center '
      style={{gap: '8px', position: 'absolute', left: '13px'}}
      {...innerProps}
    >
      <img
        alt={`${data.title}-logo`}
        src={toAbsoluteUrl(data.logoURL as string)}
        className=''
        style={{height: '16px', width: '16px'}}
      />
      <span className='fw-medium fs-6' style={{color: '#4b5675'}}>
        {data.title}
      </span>
    </div>
  )
}

const filterOption = (option: FilterOptionOption<CustomOptionType>, inputValue: string) => {
  return option.data.title.toLowerCase().includes(inputValue.toLowerCase())
}

const SUPPORTED_IMAGE_FORMATS = ['image/jpg', 'image/jpeg', 'image/png']

const initialValues = {
  id: '',
  title: '',
  logoName: '',
  logoURL: '',
  status: 'Compliant',
}

const validationSchema = Yup.object({
  title: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Name is required'),
  logoURL: Yup.mixed<File | string>()
    .required('Logo is required')
    .test('fileFormat', 'Unsupported format for logo', (value) => {
      if (typeof value === 'string') return true

      if (value instanceof File) {
        return value && SUPPORTED_IMAGE_FORMATS.includes(value.type)
      }
      return false
    })
    .test('fileSize', 'Logo size is too large', (value) => {
      if (typeof value === 'string') return true

      if (value instanceof File) {
        return value && value.size <= 5 * 1024 * 1024
      }
      return false
    }),
  // .test('dimensions', 'Logo dimensions should be 48*48 pixels.', async (value) => {
  //   if (value instanceof File) {
  //     try {
  //       const {width, height} = await getImageDimensions(value)
  //       return width === 48 && height === 48
  //     } catch (err) {
  //       return false
  //     }
  //   }
  // }),
  status: Yup.string()
    .oneOf(['Compliant', 'Ongoing'], 'Status mus be either compliant or ongoing')
    .required('Status is required'),
})

const AddEditFrameworkModal: FC<Props> = ({
  isModalOpen,
  hideModal,
  isEditMode,
  framework,
  availableFrameworks,
}) => {
  const {currentUser} = useAuth()

  const [loading, setLoading] = useState(false)

  const [isCustomFrameworkOptionSelected, setIsCustomFrameworkOptionSelected] = useState(false)

  const [uploadedFile, setUploadedFile] = useState<File>()

  const [selectedOption, setSelectedOption] = useState<SingleValue<CustomOptionType>>()

  const [selectError, setSelectError] = useState('')

  const [isDropzoneShownForEditMode, setIsDropzoneShownForEditMode] = useState(false)

  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()
  const todaysDate = month + '/' + day + '/' + year

  const formik = useFormik<FrameworkDataType>({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        const tenantId = currentUser?.tenantId || ''
        const frameworksRef = collection(
          db,
          'tenants',
          tenantId,
          'trust_center',
          'frameworks',
          'all_frameworks'
        )

        if (!isEditMode) {
          let downloadURL = ''
          let fileName = ''
          let fileId = ''

          if (values.logoURL instanceof File) {
            fileName = values.logoURL.name
            fileId = `${Date.now()}`
            const fileRef = ref(
              storage,
              `tenants/${currentUser?.tenantId}/trust_center/frameworks/${fileId}-${fileName}`
            )
            const uploadResult = await uploadBytes(fileRef, values.logoURL)
            downloadURL = await getDownloadURL(uploadResult.ref)
          }

          const newDocRef = await addDoc(frameworksRef, {
            title: values.title,
            logoName: downloadURL.length > 0 ? fileName : values.logoName,
            logoURL: downloadURL.length > 0 ? downloadURL : values.logoURL,
            logoId: downloadURL.length > 0 ? fileId : '',
            status: values.status,
            isCustom: downloadURL.length > 0 ? true : false,
          })

          await updateDoc(newDocRef, {
            id: newDocRef.id,
          })

          const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
          await addDoc(activitiesRef, {
            message: `${values.title} framework for trust center has been added`,
            timestamp: serverTimestamp(),
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            username: currentUser?.userName,
            date: todaysDate,
            email: currentUser?.email,
            target: 'Trust Center',
            action: 'Framework added',
          })

          showSuccessToast({message: 'New framework has been added successfully'})
        } else {
          const docRef = doc(frameworksRef, values.id)
          let isOldFileDeleted = false

          if (framework?.isCustom && framework?.logoURL !== values.logoURL) {
            const fileRef = ref(
              storage,
              `tenants/${currentUser?.tenantId}/trust_center/frameworks/${framework?.logoId}-${framework?.logoName}`
            )

            await deleteObject(fileRef)

            isOldFileDeleted = true
          }

          let downloadURL = ''
          let fileName = ''
          let fileId = ''

          if (values.logoURL instanceof File) {
            fileName = values.logoURL.name
            fileId = `${Date.now()}`

            const fileRef = ref(
              storage,
              `tenants/${currentUser?.tenantId}/trust_center/frameworks/${fileId}-${fileName}`
            )
            const uploadResult = await uploadBytes(fileRef, values.logoURL!)
            downloadURL = await getDownloadURL(uploadResult.ref)
          }

          await setDoc(docRef, {
            id: docRef.id,
            title: values.title,
            logoName: downloadURL.length > 0 ? fileName : isOldFileDeleted ? '' : values.logoName,
            logoURL: downloadURL.length > 0 ? downloadURL : values.logoURL,
            logoId: downloadURL.length > 0 ? fileId : isOldFileDeleted ? '' : values.logoId,
            status: values.status,
            isCustom: downloadURL.length > 0 ? true : isOldFileDeleted ? false : values?.isCustom,
          })

          const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
          await addDoc(activitiesRef, {
            message: `${framework?.title} framework for trust center has been updated`,
            timestamp: serverTimestamp(),
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            username: currentUser?.userName,
            date: todaysDate,
            email: currentUser?.email,
            target: 'Trust Center',
            action: 'Framework updated',
          })

          showSuccessToast({message: 'Framework has been updated successfully'})
        }
      } catch (error) {
        console.log('Error while updating policy', error)
      } finally {
        setLoading(false)
        hideModal()
      }
    },
  })

  useEffect(() => {
    if (framework?.title) {
      formik.setValues({
        id: framework!.id,
        title: framework!.title,
        status: framework!.status,
        logoName: framework.logoName,
        logoURL: framework!.logoURL as string,
        logoId: framework!.logoId,
        isCustom: framework.isCustom ?? false,
      })
    }

    setSelectedOption(() => {
      return isEditMode
        ? framework?.isCustom ?? false
          ? {
              title: 'Custom Framework',
              logoURL: '/media/icon/cube.svg',
            }
          : {id: framework?.id, title: framework!.title, logoURL: framework!.logoURL as string}
        : null
    })

    if (framework?.isCustom) {
      setIsCustomFrameworkOptionSelected(true)
    }
  }, [framework])

  const okBtnClickHandler = () => {
    if (!selectedOption) {
      setSelectError('Framework is required')
    }
    formik.submitForm()
  }

  const handleFrameworkChange = (option: SingleValue<CustomOptionType>) => {
    formik.resetForm()
    setSelectedOption(option)
    setSelectError('')
    if (option?.title.toLowerCase() === 'custom framework') {
      setIsCustomFrameworkOptionSelected(true)
      formik.setFieldValue('logoURL', null)
    } else {
      setIsCustomFrameworkOptionSelected(false)
      formik.setFieldValue('title', option?.title)
      formik.setFieldValue('logoURL', option?.logoURL)
    }
  }

  const handleRemoveFile = () => {
    setUploadedFile(undefined)
    formik.setFieldValue('logoURL', null)
    setIsDropzoneShownForEditMode(true)
  }

  const handleUpload = (acceptedFile: File[]) => {
    setUploadedFile(acceptedFile[0])
    formik.setFieldTouched('logoURL', true)
    formik.setFieldValue('logoURL', acceptedFile[0])
  }

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={hideModal}
      title={`${isEditMode ? 'Edit Framework' : 'Add Framework'}`}
      okBtnText={`${isEditMode ? 'Save' : 'Add Framework'}`}
      okBtnClickHandler={okBtnClickHandler}
      isCloseIconShown={true}
      isLoading={loading}
      isLoadingBtnText={`${isEditMode ? 'Saving...' : 'Adding Framework...'}`}
    >
      <div
        className='d-flex flex-column align-items-center justify-content-center'
        style={{gap: '20px'}}
      >
        <Select<CustomOptionType>
          label='Select Framework'
          options={[
            ...availableFrameworks,
            {
              title: 'Custom Framework',
              logoURL: '/media/icon/cube.svg',
            },
          ]}
          placeholder='Select Framework'
          optionComponent={CustomOptionComponent}
          singleValueComponent={SingleValueComponent}
          menuListComponent={menuListComponent}
          filterOption={filterOption}
          onChange={(value) => {
            handleFrameworkChange(value as SingleValue<CustomOptionType>)
          }}
          value={selectedOption}
          isDisabled={isEditMode}
          isClearable={false}
          error={selectError}
        />

        {isCustomFrameworkOptionSelected && (
          <>
            <Input
              type='text'
              label='Framework Name'
              placeholder='Framework Name'
              error={formik.touched.title && formik.errors.title ? formik.errors.title : ''}
              {...formik.getFieldProps('title')}
            />

            <div className='w-100 d-flex flex-column' style={{gap: '8px'}}>
              <label className='fw-medium text-dark fs-6'>Logo</label>

              {(isEditMode && framework?.isCustom && !isDropzoneShownForEditMode) ||
              uploadedFile ? (
                <div
                  className=''
                  style={{border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px'}}
                >
                  <div className='d-flex align-items-center' style={{gap: '16px'}}>
                    <img
                      src={
                        uploadedFile
                          ? URL.createObjectURL(uploadedFile as File)
                          : toAbsoluteUrl(framework?.logoURL as string)
                      }
                      alt='framework-image'
                      className='object-fit-cover'
                      style={{borderRadius: '12px', width: '48px', height: '48px'}}
                    />

                    <span className='flex-fill text-dark fs-6 fw-medium'>
                      {uploadedFile?.name ?? framework?.logoName}
                    </span>

                    <Button
                      style={{
                        width: '34px',
                        height: '34px',
                      }}
                      onClick={handleRemoveFile}
                    >
                      <img
                        alt='trash-icon'
                        src={toAbsoluteUrl('/media/icon/trash.svg')}
                        className=''
                        style={{height: '18px', width: '18px'}}
                      />
                    </Button>
                  </div>
                </div>
              ) : (
                <Dropzone
                  onDrop={handleUpload}
                  minSize={1024}
                  accept={{
                    'image/png': ['.png'],
                    'image/jpeg': ['.jpg', '.jpeg'],
                  }}
                  maxSize={5 * 1024 * 1024}
                >
                  {({getRootProps, getInputProps}) => (
                    <div
                      {...getRootProps({
                        className: 'dropzone',
                        style: {
                          backgroundColor: 'white',
                          border: '2px dashed #E5E7EB',
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '24px 32px',
                          textAlign: 'center',
                          cursor: 'pointer',
                        },
                      })}
                    >
                      <input {...getInputProps()} />

                      <div className='d-flex flex-column align-items-center' style={{gap: '16px'}}>
                        <div className='d-flex flex-column align-items-center' style={{gap: '7px'}}>
                          <div
                            className='d-flex align-items-center justify-content-center'
                            style={{
                              backgroundColor: '#F3F4F6',
                              borderRadius: '12px',
                              height: '48px',
                              width: '48px',
                            }}
                          >
                            <img
                              alt='image-placeholder-icon'
                              src={toAbsoluteUrl('/media/icon/image-placeholder.svg')}
                              className=''
                            />
                          </div>

                          <span className='text-dark fs-6 fw-medium'>Drag and drop file here</span>

                          <span className='text-secondary-gray' style={{fontSize: '13px'}}>
                            48x48 px recommended. Supported files: jpg, jpeg, png
                          </span>
                        </div>

                        <div>
                          <Button>
                            <img
                              alt='upload-icon'
                              src={toAbsoluteUrl('/media/icon/upload.svg')}
                              className=''
                              style={{height: '18px', width: '18px'}}
                            />
                            <span className='text-dark fs-6 fw-medium'>Upload File</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </Dropzone>
              )}

              {formik.touched.logoURL && formik.errors.logoURL && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.logoURL}</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className='w-100 d-flex flex-column justify-content-center' style={{gap: '8px'}}>
          <label className='fw-medium text-dark fs-6'>Status</label>

          <div role='radio' className='d-flex align-items-center' style={{gap: '12px'}}>
            {statusArr.map((status, idx) => (
              <div
                key={idx}
                className='flex-fill cursor-pointer'
                style={{
                  backgroundColor: `${formik.values.status === status.title ? '#F9FAFB' : 'white'}`,
                  border: `${
                    formik.values.status === status.title ? '2px solid black' : '2px solid #E5E5E5'
                  }`,
                  borderRadius: '8px',
                  padding: '8px 16px',
                }}
                onClick={() => {
                  formik.setFieldValue('status', status.title)
                }}
              >
                <div
                  className='d-flex justify-content-center align-items-center'
                  style={{gap: '6px'}}
                >
                  <img
                    alt={'icon'}
                    src={toAbsoluteUrl(status.iconPath)}
                    className=''
                    style={{height: '16px', width: '16px'}}
                  />
                  <span className='text-dark fs-6'>{status.title}</span>
                </div>
              </div>
            ))}
          </div>

          {formik.touched.status && formik.errors.status && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.status}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </CustomModal>
  )
}

export default AddEditFrameworkModal
